import { createBackground, getCreativeView, renderCreativeView } from './core';
import type { CreativeAsset } from './core';
import { preloadImages } from '../lib/utils';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import { Card } from 'src/game/cards/components/Card';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import type { CardShareKind } from 'src/lib/sharing';
import { cardsCatalog, type CardId } from 'src/replicant/cards/cards.ruleset';
import { cardsGetCardIcon } from 'src/game/cards/cards.assets';

const CREATIVE_WIDTH = 1040;
const CREATIVE_HEIGHT = 578;

/**
 * Wait for Card.setState side effects to reach the renderer before snapshotting.
 * Card uses LazyImageView plus a zero-duration flip animation, and bitmap font
 * glyphs also need a render tick before renderToDataURL captures them.
 */
function waitAFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

/**
 * Render a card share image for request, donate, or trade sharing.
 *
 * @param args Card share kind, card, and localized CTA to render.
 * @returns Rendered creative asset ready to upload.
 */
export async function createCardShareCreative(args: {
  kind: CardShareKind;
  cardId: CardId;
  cta: string;
}): Promise<CreativeAsset> {
  const { kind, cardId, cta } = args;
  const superview = getCreativeView(CREATIVE_WIDTH, CREATIVE_HEIGHT);

  const background = `assets/creatives/cards/cards_${kind}_bg.png`;
  const icon = `assets/creatives/cards/cards_${kind}_icon.png`;
  const card = cardsGetCardIcon(cardId);
  const cardRarity = cardsCatalog[cardId].rarity;

  await preloadImages([
    background,
    card,
    icon,
    'assets/ui/cards/common/card_slot.png',
    'assets/ui/cards/common/card_back.png',
    'assets/ui/cards/common/card_title.png',
    `assets/ui/cards/common/icon_star${cardRarity}.png`,
  ]);

  createBackground({ superview, image: background });

  const cardView = new Card({
    superview,
    x: CREATIVE_WIDTH / 2,
    y: 222,
    scale: 1.45,
    zIndex: 2,
    centerOnOrigin: true,
    r: -0.14,
  });
  cardView.setState({ id: cardId, count: 1 });

  new ImageScaleView({
    superview,
    image: icon,
    x: 607,
    y: 108,
    width: 96,
    height: 97,
    zIndex: 3,
  });

  new LangBitmapFontTextView({
    superview,
    font: 'Body',
    color: '#FFFFFF',
    size: 60,
    localeText: () => cta,
    align: 'center',
    verticalAlign: 'center',
    x: 394,
    y: 449,
    width: 298,
    height: 78,
    zIndex: 4,
  });

  // Let card image, flip state, and bitmap text flush before capturing.
  await waitAFrame();

  return renderCreativeView(`cards_${kind}_${cardId}`, superview);
}
